/**
 * Centre of the loadout screen: the ranger and the supply pods as physical containers (08 §2.5),
 * each with a live weight bar and one stepper per item. Pods are colour-tagged A/B/C with the
 * letter always printed - the same tags the flyover and the HUD use. A view: it paints a state.
 */

import type { PriceList } from '../../../meta/api';
import { box, el } from '../dom';
import { weightBar } from '../widgets/gauge';
import type { WeightBar } from '../widgets/gauge';
import { stepper } from '../widgets/stepper';
import type { Stepper } from '../widgets/stepper';
import type { AddBlock, ContainerIndex, LoadoutState, PackItem } from './model';
import { capOf, containerWeight, contentsOf, PACK_ITEMS, POD_LETTERS, RANGER_CONTAINER } from './model';

export interface ContainersOptions {
  pods: number;
  prices: PriceList;
  /** Item name from `GameApi.describe`. */
  name(item: PackItem): string;
  /** Why one more of `item` cannot go into `container` now, or null. */
  blockOf(state: LoadoutState, container: ContainerIndex, item: PackItem): AddBlock;
  onStep(container: ContainerIndex, item: PackItem, delta: 1 | -1): void;
}

export interface ContainersView {
  readonly el: HTMLElement;
  update(state: LoadoutState): void;
}

/** What a blocked plus says about itself. */
export function blockText(block: AddBlock): string {
  if (block === 'weight') return 'OVER WEIGHT';
  return block === 'rp' ? 'NOT ENOUGH RP' : '';
}

export function containerTitle(container: ContainerIndex): string {
  return container === RANGER_CONTAINER ? 'THE RANGER' : `POD ${POD_LETTERS[container] ?? container + 1}`;
}

interface Built {
  container: ContainerIndex;
  bar: WeightBar;
  steppers: Array<[item: PackItem, stepper: Stepper]>;
}

export function containersView(options: ContainersOptions): ContainersView {
  const root = el('div', 'menu-load-containers');
  const built: Built[] = [];

  const build = (container: ContainerIndex): void => {
    const letter = container === RANGER_CONTAINER ? '' : (POD_LETTERS[container] ?? '');
    const kind = letter === '' ? 'menu-load-box-ranger' : `menu-load-pod-${letter.toLowerCase()}`;
    const node = el('div', `menu-load-box ${kind}`);
    node.setAttribute('role', 'group');
    node.setAttribute('aria-label', containerTitle(container));
    const head = el('div', 'menu-load-box-head');
    if (letter !== '') head.append(el('span', 'menu-load-tag', letter));
    head.append(el('span', 'menu-load-box-title', containerTitle(container)));
    // The gauge read "22/30" with no unit: weight, not a count of things. Say which.
    head.append(el('span', 'menu-load-box-unit menu-dim', 'WT'));
    const bar = weightBar();
    node.append(head, bar.el);

    const steppers: Built['steppers'] = [];
    for (const item of PACK_ITEMS) {
      const itemName = options.name(item);
      const what = `${itemName} - ${containerTitle(container)}`;
      const s = stepper(what, (delta) => options.onStep(container, item, delta));
      const label = el('span', 'menu-load-item', itemName);
      label.title = itemName;
      node.append(box('menu-load-line', label, s.el));
      steppers.push([item, s]);
    }
    root.append(node);
    built.push({ container, bar, steppers });
  };

  build(RANGER_CONTAINER);
  for (let i = 0; i < options.pods; i++) build(i);
  if (options.pods === 0) {
    root.append(el('div', 'menu-load-nopods menu-dim menu-prose', 'NO SUPPLY PODS ON THIS MISSION.'));
  }

  return {
    el: root,
    update(state): void {
      for (const b of built) {
        const contents = contentsOf(state, b.container);
        if (contents === undefined) continue;
        // The ranger's bar includes his specialist gear: it rides on him, never in a pod.
        b.bar.set(containerWeight(state, b.container, options.prices), capOf(b.container, options.prices));
        for (const [item, s] of b.steppers) {
          const block = options.blockOf(state, b.container, item);
          s.set(contents[item], contents[item] > 0, block === null, blockText(block));
        }
      }
    },
  };
}
